import { useCallback, useEffect, useRef, useState } from "react";
import type {
  JournalAiMode,
  JournalSectionAiResult,
} from "@care-by-ella/contracts";
import {
  AiAssistDisabledError,
  fetchAiFormOverview,
  generateTransferJournalSectionAi,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  aiAvailabilityFrom,
  journalSectionAiRequest,
} from "@/lib/journal-ai-request";
import { useReadOnly } from "@/components/SupportView";

// TES-1034 — the per-heading AI help for the transfer review's note, or
// undefined when it is not offered (the card then shows no AI buttons and the
// note is exactly the deterministic prefill).
//
// Availability is probed the way the meeting learns it: the forms overview
// read, which never calls the model and 404s with `ai_assist_disabled` when
// the switch is off (there is no config endpoint). The transfer tab does not
// mount the overview panel, so it asks once itself.
//
// TES-1040 — never in an admin's support view: AI is out of scope there and
// the overview route answers her 403, so the probe is not made at all.
export function useTransferJournalAi(
  patientId: string,
  onUnauthorized: () => void,
):
  | ((
      key: string,
      mode: JournalAiMode,
      text: string,
    ) => Promise<JournalSectionAiResult>)
  | undefined {
  const readOnly = useReadOnly();
  const [available, setAvailable] = useState<boolean | null>(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    if (readOnly) return;
    let live = true;
    fetchAiFormOverview(patientId)
      .then(() => live && setAvailable(aiAvailabilityFrom("ok")))
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        setAvailable(
          aiAvailabilityFrom(
            e instanceof AiAssistDisabledError ? "disabled" : "error",
          ),
        );
      });
    return () => {
      live = false;
    };
  }, [patientId, readOnly]);

  const sectionAi = useCallback(
    (key: string, mode: JournalAiMode, text: string) =>
      generateTransferJournalSectionAi(
        patientId,
        journalSectionAiRequest(key, mode, text),
      ),
    [patientId],
  );
  return available && !readOnly ? sectionAi : undefined;
}
